import React, { useState, useEffect, useRef } from 'react';
import { ANIMATION } from '../constants';

interface ScoreDisplayProps {
  round: number;
  totalRounds: number;
  correctGuesses: number;
  score: number;
  pointsGained?: number | null;
  onShowInstructions?: () => void;
  gameMode: 'daily';
  isPastChallenge?: boolean;
}

// Animation component for the gas-pump style digit flip
const AnimatedDigit: React.FC<{
  value: number;
  animationDuration?: number;
}> = ({ value, animationDuration = 800 }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValueRef = useRef(value);

  useEffect(() => {
    if (value !== previousValueRef.current) {
      const steps = 20; // Increased from 10 to 20 for smoother animation
      const stepTime = animationDuration / steps;
      const increment = (value - previousValueRef.current) / steps;
      let currentValue = previousValueRef.current;

      const animationInterval = setInterval(() => {
        currentValue += increment;
        if (
          (increment > 0 && currentValue >= value) ||
          (increment < 0 && currentValue <= value) ||
          increment === 0
        ) {
          setDisplayValue(value);
          clearInterval(animationInterval);
        } else {
          setDisplayValue(Math.round(currentValue));
        }
      }, stepTime);

      previousValueRef.current = value;

      return () => clearInterval(animationInterval);
    }
  }, [value, animationDuration]);

  // Only show the value if it's not 0 or if it's a natural 0 in the number
  const displayText = displayValue === 0 ? '0' : displayValue.toString();

  return <span className="inline-block">{displayText}</span>;
};

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({
  round,
  totalRounds,
  correctGuesses,
  score,
  pointsGained,
  onShowInstructions,
  gameMode,
  isPastChallenge,
}) => {
  const [showPointsGained, setShowPointsGained] = useState(false);
  const scoreRef = useRef<HTMLDivElement>(null);

  const getPointsPosition = (scoreValue: number) => {
    const numDigits = Math.max(1, Math.floor(Math.log10(scoreValue)) + 1);
    return `${numDigits * 8 + 12}px`;
  };

  // Show points gained animation when points change
  useEffect(() => {
    // Check specifically for non-null, non-undefined and greater than 0
    if (pointsGained != null && pointsGained > 0) {
      setShowPointsGained(true);
      const timer = setTimeout(() => {
        setShowPointsGained(false);
      }, ANIMATION.SCORE_FADE_DURATION); // Duration of the fadeUpAndOut animation

      return () => clearTimeout(timer);
    } else {
      // Explicitly hide if pointsGained is 0, null, or undefined
      setShowPointsGained(false);
    }
  }, [pointsGained]); // Depend only on pointsGained

  const handleLogoClick = () => {
    if (isPastChallenge) {
      window.location.href = '/';
    }
  };

  const logoClass = isPastChallenge ? 'cursor-pointer' : '';

  return (
    <div className="flex md:justify-between md:pr-8 w-full items-center">
      <div
        className={`flex flex-col items-center leading-none gap-[6px] md:mb-[-2px] mb-0 ${logoClass}`}
        onClick={handleLogoClick}
      >
        <div className="items-center self-start mb-[-2px] hidden md:flex">
          <h1 className="text-white font-black uppercase leading-none">
            <span>WHICH</span>{' '}
            <span className="text-[#C6A4FF] ml-[-3px]">YEAR</span>
          </h1>
        </div>
        <div className="items-center self-start mb-[-2px] flex md:hidden p-2 bg-[#1A1321] text-[22px] rounded-lg">
          <h1 className="text-white font-black uppercase leading-none">
            <span>W</span> <span className="text-[#C6A4FF] ml-[-6px]">Y</span>
          </h1>
        </div>
        <span className="text-white/70 uppercase font-bold text-[12px] leading-none hidden md:block">
          Daily Challenge
        </span>
      </div>
      {/* Only show round and score if not completed challenge (totalRounds > 0) */}
      {totalRounds > 0 && (
        <div className="flex space-x-5 md:space-x-6 items-center">
          {/* Round Indicator */}
          <div className="flex-col leading-none gap-[5px] hidden md:flex">
            <span className="text-white/70 uppercase font-bold text-xs leading-none">
              Round
            </span>
            <div className="flex items-baseline leading-none ">
              <span className="font-bold text-white text-base leading-none">
                {round}
              </span>
              <span className="text-white/70 font-bold text-xs leading-none">
                /{totalRounds}
              </span>
            </div>
          </div>
          <div className="flex flex-col leading-none gap-[5px]">
            <span className="text-white/70 uppercase font-bold text-xs leading-none text-left">
              Score
            </span>
            <div className="font-bold text-white text-base flex items-center leading-none relative">
              <div ref={scoreRef} className="relative">
                <AnimatedDigit value={score} />
                {showPointsGained && pointsGained && pointsGained > 0 && (
                  <div
                    className="text-[#FFC300] text-xs md:hidden font-bold uppercase leading-none absolute"
                    style={{
                      animation: 'fadeUpAndOut 2s ease-out forwards',
                      left: getPointsPosition(score),
                      bottom: '-8px',
                    }}
                  >
                    +{pointsGained}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreDisplay;
